// Runtime branding: applies an admin-configured accent colour, favicon and
// page title without a rebuild. Accent is injected as CSS custom properties on
// the document root, overriding the defaults in tokens/theme.css.

export const DEFAULT_ACCENT = '#6366f1';
const HEX_RE = /^#[0-9a-f]{6}$/i;

// Resolve a possibly-relative asset path (e.g. "/uploads/logo.png") against the
// current origin so it works in <img>/<link> regardless of API base.
export function resolveAssetUrl(url) {
  if (!url) return '';
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:')) return url;
  return url.startsWith('/') ? url : `/${url}`;
}

// Darken a #rrggbb colour by `amount` (0..1) for the hover/active accent shade.
export function darkenHex(hex, amount = 0.12) {
  if (!HEX_RE.test(hex)) return hex;
  const n = parseInt(hex.slice(1), 16);
  const f = 1 - amount;
  const r = Math.max(0, Math.round(((n >> 16) & 0xff) * f));
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * f));
  const b = Math.max(0, Math.round((n & 0xff) * f));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function applyAccent(accentColor) {
  const root = document.documentElement;
  if (accentColor && HEX_RE.test(accentColor)) {
    root.style.setProperty('--accent-primary', accentColor);
    root.style.setProperty('--accent-primary-dark', darkenHex(accentColor, 0.14));
    root.style.setProperty('--accent-tint', `${accentColor}26`); // ~15% alpha
  } else {
    // Fall back to the stylesheet defaults.
    root.style.removeProperty('--accent-primary');
    root.style.removeProperty('--accent-primary-dark');
    root.style.removeProperty('--accent-tint');
  }
}

function applyFavicon(faviconUrl) {
  if (!faviconUrl) return;
  const href = resolveAssetUrl(faviconUrl);
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
}

export function applyBranding({ accentColor, faviconUrl, title } = {}) {
  applyAccent(accentColor);
  applyFavicon(faviconUrl);
  if (title) document.title = title;
}
